import React from "react";
import { useAuth } from "react-oidc-context";
import { Navigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { isAmxAdmin, isAmxUser, requiresPartnerOnboarding } from "../utils/auth";

function AuthLoading({ message = 'Loading...' }) {
    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'grid',
                placeItems: 'center',
                bgcolor: 'background.default',
                color: 'text.primary',
                p: 3,
            }}
        >
            <Box sx={{ display: 'grid', justifyItems: 'center', gap: 2 }}>
                <CircularProgress size={32} />
                <Typography variant="body2" color="text.secondary">
                    {message}
                </Typography>
            </Box>
        </Box>
    );
}

const RequireAuth = ({ children }) => {
    // Check if user is actually authenticated with Cognito
    const auth = useAuth();
    const location = useLocation();
    const storedUser = useSelector((state) => state.auth.user);
    const partnerProfile = useSelector((state) => state.auth.partnerProfile);
    const partnerProfileLoadStatus = useSelector((state) => state.auth.partnerProfileLoadStatus);

    if (auth.isLoading) {
        return <AuthLoading />;
    }

    if (!auth.isAuthenticated) {
        return <Navigate to="/welcome" replace />;
    }

    const activeUser = storedUser || auth.user?.profile;
    const adminOnly = isAmxAdmin(activeUser) && !isAmxUser(activeUser);
    const needsPartnerProfile = isAmxUser(activeUser) && !isAmxAdmin(activeUser);
    const onboardingPath = location.pathname === '/partner-onboarding';
    const adminAllowedPath = location.pathname.startsWith('/admin') || location.pathname === '/my-account';

    if (adminOnly && !adminAllowedPath) {
        return <Navigate to="/admin" replace />;
    }

    if (needsPartnerProfile && !partnerProfile && partnerProfileLoadStatus !== 'error') {
        return <AuthLoading message="Loading account profile..." />;
    }

    if (needsPartnerProfile && partnerProfileLoadStatus === 'error') {
        return (
            <Box
                sx={{
                    minHeight: '100vh',
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor: 'background.default',
                    color: 'text.primary',
                    p: 3,
                    textAlign: 'center',
                }}
            >
                <Box sx={{ maxWidth: 520 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                        Account profile unavailable
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        AMX could not load your partner profile.
                    </Typography>
                </Box>
            </Box>
        );
    }

    if (requiresPartnerOnboarding(partnerProfile) && !onboardingPath) {
        return <Navigate to="/partner-onboarding" replace state={{ from: location }} />;
    }

    if (!requiresPartnerOnboarding(partnerProfile) && onboardingPath) {
        return <Navigate to="/" replace />;
    }

    return children;
};

export default RequireAuth;
